import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Customer, CustomerRelationshipLog } from '../../types/customer';
import { ClockIcon, UserIcon, PlusIcon, PencilIcon, ExclamationCircleIcon, CheckCircleIcon, FunnelIcon } from '@heroicons/react/24/outline';
import CustomerRelationshipLogForm from './CustomerRelationshipLogForm';
import { doc, updateDoc, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import EditRelationshipLogModal from './EditRelationshipLogModal';
import { userService } from '../../services/userService';


interface CustomerRelationshipLogTabProps {
  logs: CustomerRelationshipLog[] | undefined;
  customerId: string;
  customerName: string;
  customer: Customer;
  onLogsUpdated: () => Promise<void>;
}

const CustomerRelationshipLogTab: React.FC<CustomerRelationshipLogTabProps> = ({ 
  logs, 
  customerId,
  customerName,
  customer,
  onLogsUpdated
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logToEdit, setLogToEdit] = useState<CustomerRelationshipLog | null>(null);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [filterOpenOnly, setFilterOpenOnly] = useState(false);

  // Load user names for displaying in logs
  const loadUserNames = useCallback(async (logs: CustomerRelationshipLog[]) => {
    const uniqueUserIds = Array.from(new Set(logs.map(l => l.createdBy).filter(Boolean)));
    const newUserNames: Record<string, string> = {};

    for (const userId of uniqueUserIds) {
      if (!userNames[userId] && !userId.includes('@')) { // Skip if already loaded or if it's an email
        try {
          const userData = await userService.getUser(userId);
          if (userData) {
            newUserNames[userId] = userData.displayName || userData.email || 'Ismeretlen felhasználó';
          } else {
            newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          newUserNames[userId] = `Felhasználó ${userId.slice(-6)}`;
        }
      }
    }

    if (Object.keys(newUserNames).length > 0) {
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    }
  }, [userNames]);

  // Load user names when logs change
  useEffect(() => {
    if (logs && logs.length > 0) {
      loadUserNames(logs);
    }
  }, [logs, loadUserNames]);

  // Reset logToEdit when it's null
  useEffect(() => {
    if (!showEditModal) {
      setLogToEdit(null);
    }
  }, [showEditModal]);

  // Function to generate the entry ID based on requirements
  const generateEntryId = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    
    // Get today's logs to calculate the sequence number
    const todayLogs = logs?.filter(log => {
      const logDate = log.createdAt instanceof Date 
        ? log.createdAt 
        : log.createdAt.toDate();
      return logDate.getFullYear() === date.getFullYear() &&
             logDate.getMonth() === date.getMonth() &&
             logDate.getDate() === date.getDate();
    }) || [];
    
    // Calculate sequence number (todayLogs.length + 1, formatted as 4 digits)
    const seqNum = String(todayLogs.length + 1).padStart(4, '0');
    
    // Determine prefix (first 8 chars of tax number or customer ID)
    let prefix = '';
    if (customer.type === 'organization' && customer.taxNumber) {
      prefix = customer.taxNumber.substring(0, 8);
    } else {
      prefix = customer.id.substring(0, 8);
    }
    
    return `${prefix}_${dateStr}_${seqNum}`;
  };

  const handleAddLog = async (content: string) => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const newLogEntry: CustomerRelationshipLog = {
        id: uuidv4(),
        customerId,
        entryId: generateEntryId(),
        content,
        createdAt: Timestamp.now(),
        createdBy: user.uid,
        createdByName: user.displayName || undefined,
        isOpen: true // New logs are open by default
      };
      
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        relationshipLogs: arrayUnion(newLogEntry)
      });
      
      setShowAddForm(false);
      // Wait for the onLogsUpdated callback to complete
      await onLogsUpdated();
      toast.success(t('customers.relationshipLog.addSuccess') || 'Log entry added successfully');
    } catch (error) {
      console.error('Failed to add log entry:', error);
      toast.error(t('customers.relationshipLog.addError') || 'Failed to add log entry');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
  };

  // Function to handle editing a log entry
  const handleEditClick = (log: CustomerRelationshipLog) => {
    setLogToEdit(log);
    setShowEditModal(true);
  };

  // Handle successful edit from EditRelationshipLogModal
  const handleEditSuccess = async () => {
    await onLogsUpdated();
    setShowEditModal(false);
  };

  // Filtered logs based on filter settings
  const filteredLogs = logs 
    ? [...logs].filter(log => !filterOpenOnly || log.isOpen)
    : [];

  // Sort logs by creation date (newest first)
  const sortedLogs = filteredLogs.sort((a, b) => {
    const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt.toDate();
    const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt.toDate();
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="space-y-4 p-4">
      {/* Header with controls */}
      <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-200">
          {t('customers.relationshipLog.title') || 'Customer Relationship Log'}
        </h3>
        
        <div className="flex items-center gap-3">
          {/* Filter toggle */}
          <button
            type="button"
            onClick={() => setFilterOpenOnly(!filterOpenOnly)}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              filterOpenOnly 
                ? 'border-amber-300 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 focus:ring-amber-500' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-primary-500'
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-1" />
            {t('customers.relationshipLog.filterOpenOnly') || 'Open tasks only'}
            {filterOpenOnly && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs">
                {sortedLogs.length}
              </span>
            )}
          </button>
          
          {/* Add button */}
          {!showAddForm && (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {t('customers.relationshipLog.add') || 'Add new log entry'}
            </button>
          )}
        </div>
      </div>

      {showAddForm && (
        <CustomerRelationshipLogForm 
          customer={customer}
          onAddLog={handleAddLog}
          onCancel={handleCancelEdit}
        />
      )}

      {sortedLogs.length === 0 && !showAddForm ? (
        <div className="p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {filterOpenOnly 
              ? t('customers.relationshipLog.noOpenLogs') || 'No open tasks found for this customer.' 
              : t('customers.relationshipLog.noLogs') || 'No relationship logs found for this customer.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden">
          {sortedLogs.map((log) => {
            const createdAt = log.createdAt instanceof Date ? log.createdAt : log.createdAt.toDate();
            const formattedDate = new Intl.DateTimeFormat('hu-HU', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            }).format(createdAt);

            const isCurrentUserCreator = user && log.createdBy === user.uid;

            return (
              <div key={log.id} className={`mb-4 border rounded-lg overflow-hidden ${
                log.isOpen ? 'border-amber-200 dark:border-amber-600' : 'border-green-200 dark:border-green-600'
              }`}>
                <div className={`px-4 py-2 border-b ${
                  log.isOpen 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-600' 
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-600'
                }`}>
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-primary-700 dark:text-primary-400">{log.entryId}</div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                      <span>{formattedDate}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" />
                        <span>{log.createdByName || userNames[log.createdBy] || log.createdBy}</span>
                      </div>
                      
                      {/* Task status indicator */}
                      <div className={`flex items-center text-sm ${
                        log.isOpen ? 'text-amber-700 dark:text-amber-400' : 'text-green-700 dark:text-green-400'
                      }`}>
                        {log.isOpen ? (
                          <>
                            <ExclamationCircleIcon className="h-4 w-4 mr-1 text-amber-500 dark:text-amber-400" />
                            <span>{t('customers.relationshipLog.statusOpen') || 'Open'}</span>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500 dark:text-green-400" />
                            <span>{t('customers.relationshipLog.statusClosed') || 'Closed'}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Edit button */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditClick(log)}
                        disabled={isSubmitting}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        title={t('customers.relationshipLog.edit') || 'Edit'}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`p-4 overflow-hidden ${!log.isOpen ? 'bg-green-50 dark:bg-green-900/10' : 'bg-white dark:bg-gray-800'}`}>
                  <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words w-full max-w-full overflow-hidden overflow-wrap-anywhere hyphens-auto" style={{ wordBreak: 'break-word', textOverflow: 'ellipsis' }}>{log.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Relationship Log Modal */}
      <EditRelationshipLogModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        logToEdit={logToEdit}
        customer={customer}
      />
    </div>
  );
};

export default CustomerRelationshipLogTab; 